import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { AuthProvider } from './lib/session'
import App from './App'
import Boundary from './components/Boundary'
import './styles.css'

/**
 * The outermost net.
 *
 * The boundary inside `App` protects one screen and leaves the tabs usable,
 * which is the better experience — but it sits BELOW `RosterProvider`, and that
 * is where the likeliest crash in this application lives: the provider runs the
 * filter, search and predicate code over 786 agents during its own render, so a
 * single bad row throws above every boundary in the tree. That was the exact
 * blank white page the boundary was written to prevent, still reachable after
 * it was added.
 *
 * This one catches that. It has nothing left to preserve — if it fires, the
 * header and tabs are gone too — so it is a worse experience and a necessary
 * one. Nothing catches a throw from here, and there is nowhere further out to
 * put it.
 */
/**
 * PWA install support — registration only, no offline caching.
 *
 * `public/sw.js` is a deliberate pass-through: every fetch still goes to the
 * network. See that file's own comment and `pwa-mobile-build-scope` for why —
 * short version, this app already has a blank-screen guard in index.html for
 * Cloudflare's renamed-bundle-on-every-deploy behavior, and a caching service
 * worker would reintroduce that failure mode in a harder-to-diagnose form.
 *
 * Registered after `load` so it never competes with the initial render for
 * bandwidth, wrapped in a feature check for browsers without support, and
 * best-effort throughout — a failed registration should never block the app
 * from working, it only means "not installable this time."
 */
function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // Re-check for a new deploy whenever she comes back to the tab.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update().catch(() => {})
          }
        })

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing
          if (!installing) return
          installing.addEventListener('statechange', () => {
            // `controller` already existing means this is an update, not the
            // very first install — reload once so she lands on the new build
            // instead of running stale JS under a fresh service worker.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              window.location.reload()
            }
          })
        })
      })
      .catch(() => {
        // Not installable this load; the app itself is unaffected.
      })
  })
}

registerServiceWorker()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Boundary where="The dashboard" scope="alone">
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </Boundary>
  </React.StrictMode>,
)
